import { createAsyncThunk } from "@reduxjs/toolkit";
import { CustomerAPIService, DashboardAPIService } from "src/constants";
import { sendRequestJSON } from "src/lib/axios";
import { toastMessage } from "../commonSlice";
import { AppDispatch } from "src/store";

export const fetchDashboardData = createAsyncThunk(
  "dashboard/fetchDashboardData",
  async (
    { branchId, filter }: { branchId?: number; filter?: string },
    { dispatch }
  ) => {
    try {
      const queryParams = new URLSearchParams();
      if (branchId) queryParams.append("branchId", branchId.toString());
      if (filter) queryParams.append("filter", filter);

      const response = await sendRequestJSON(
        null,
        `${DashboardAPIService.GET_DASHBOARD_DATA}?${queryParams.toString()}`,
        "get"
      );

      if (response && !response.error) {
        return response?.result?.data?.data;
      } else {
        dispatch(
          toastMessage({
            error: true,
            message: response.result?.message || "An error occurred",
          })
        );
        return [];
      }
    } catch (error) {
      dispatch(
        toastMessage({
          error: true,
          message: error?.response?.data || "An unexpected error occurred",
        })
      );
      return [];
    }
  }
);

export const fetchTotalOrders = createAsyncThunk(
  "dashboard/fetchTotalOrders",
  async (
    { branchId, filter }: { branchId?: number; filter?: string },
    { dispatch }
  ) => {
    try {
      const queryParams = new URLSearchParams();
      if (branchId) queryParams.append("branchId", branchId.toString());
      if (filter) queryParams.append("filter", filter);

      const response = await sendRequestJSON(
        null,
        `${DashboardAPIService.GET_TOTAL_ORDERS}?${queryParams.toString()}`,
        "get"
      );

      if (response && !response.error) {
        return response?.result?.data;
      } else {
        dispatch(
          toastMessage({
            error: true,
            message: response.result?.message || "An error occurred",
          })
        );
        return [];
      }
    } catch (error) {
      dispatch(
        toastMessage({
          error: true,
          message: error?.response?.data || "An unexpected error occurred",
        })
      );
      return [];
    }
  }
);

export const fetchDayWiseOrders = createAsyncThunk<
  any,
  { month: number; year: number; branchId?: number; byStatus?: number },
  { dispatch: AppDispatch }
>(
  "dashboard/fetchDayWiseOrders",
  async ({ month, year, branchId, byStatus }, { dispatch }) => {
    try {
      const queryParams = new URLSearchParams();
      queryParams.append("month", month.toString());
      queryParams.append("year", year.toString());
      if (branchId) queryParams.append("branchId", branchId.toString());
      if (byStatus) queryParams.append("byStatus", byStatus.toString());

      const response = await sendRequestJSON(
        null,
        `${DashboardAPIService.GET_DAY_WISE_ORDERS}?${queryParams.toString()}`,
        "get"
      );

      if (response && !response.error) {
        // response.result.data contains { month, year, orders: [...], totalOrders }
        return response.result.data;
      }

      dispatch(
        toastMessage({
          error: true,
          message: response.result?.message || "An error occurred",
        })
      );

      return { month, year, orders: [], totalOrders: 0 };
    } catch (error: any) {
      dispatch(
        toastMessage({
          error: true,
          message: error?.response?.data || "An unexpected error occurred",
        })
      );

      return { month, year, orders: [], totalOrders: 0 };
    }
  }
);

export const fetchTotalComplaints = createAsyncThunk(
  "dashboard/fetchTotalComplaints",
  async (
    { branchId, filter }: { branchId?: number; filter?: string },
    { dispatch }
  ) => {
    try {
      const queryParams = new URLSearchParams();
      if (branchId) queryParams.append("branchId", branchId.toString());
      if (filter) queryParams.append("filter", filter);
      const response = await sendRequestJSON(
        null,
        `${DashboardAPIService.GET_TOTAL_COMPLAINTS}?${queryParams.toString()}`,
        "get"
      );
      if (response && !response.error) {
        const { data } = response?.result?.data;
        return data;
      } else {
        dispatch(
          toastMessage({
            error: true,
            message: response.result.message,
          })
        );
        return [];
      }
    } catch (error) {
      dispatch(
        toastMessage({
          error: true,
          message: error.response.data,
        })
      );
      return [];
    }
  }
);

export const fetchTotalOrdersByStatus = createAsyncThunk(
  "dashboard/fetchTotalOrdersByStatus",
  async ({ branchId }: { branchId?: number }, { dispatch }) => {
    try {
      const query = branchId ? `?branchId=${branchId}` : "";
      const response = await sendRequestJSON(
        null,
        `${DashboardAPIService.GET_TOTAL_ORDERS_BY_STATUS}${query}`,
        "get"
      );
      if (response && !response.error) {
        const { data } = response?.result?.data;
        return data;
      } else {
        dispatch(
          toastMessage({
            error: true,
            message: response.result.message,
          })
        );
        return [];
      }
    } catch (error) {
      dispatch(
        toastMessage({
          error: true,
          message: error.response.data,
        })
      );
      return [];
    }
  }
);

export const fetchTotalOrdersByServices = createAsyncThunk(
  "dashboard/fetchTotalOrdersByServices",
  async ({ branchId }: { branchId?: number }, { dispatch }) => {
    try {
      const query = branchId ? `?branchId=${branchId}` : "";
      const response = await sendRequestJSON(
        null,
        `${DashboardAPIService.GET_TOTAL_ORDERS_BY_SERVICES}${query}`,
        "get"
      );
      if (response && !response.error) {
        const { data } = response?.result?.data;
        return data;
      } else {
        dispatch(
          toastMessage({
            error: true,
            message: response.result.message,
          })
        );
        return [];
      }
    } catch (error) {
      dispatch(
        toastMessage({
          error: true,
          message: error.response.data,
        })
      );
      return [];
    }
  }
);

export const fetchTotalCustomers = createAsyncThunk(
  "dashboard/fetchTotalCustomersCount",
  async (
    { branchId, filter }: { branchId?: number; filter?: string },
    { dispatch }
  ) => {
    try {
      const queryParams = new URLSearchParams();
      if (branchId) queryParams.append("branchId", branchId.toString());
      if (filter) queryParams.append("filter", filter);

      const response = await sendRequestJSON(
        null,
        `${CustomerAPIService.GET_CUSTOMER_COUNTS}?${queryParams.toString()}`,
        "get"
      );

      if (response && !response.error) {
        return response?.result?.data?.data;
      } else {
        dispatch(
          toastMessage({
            error: true,
            message: response.result?.message || "An error occurred",
          })
        );
        return [];
      }
    } catch (error) {
      dispatch(
        toastMessage({
          error: true,
          message: error?.response?.data || "An unexpected error occurred",
        })
      );
      return [];
    }
  }
);
